import { useEffect, useMemo, useState } from 'react';
import { FiExternalLink, FiShare2 } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { upcomingEvents, pastEvents } from '../config/events';
import LightboxImage from './LightboxImage';

const VIEW_FILTERS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'all', label: 'All' },
];
const DEFAULT_VIEW_FILTER = 'all';

function Lightbox({ image, onClose }) {
  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-screen w-full max-w-5xl flex-col items-center"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center border border-white/40 bg-black/40 text-3xl leading-none text-white transition hover:bg-black/65 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Close enlarged event image"
        >
          &times;
        </button>
        <LightboxImage
          src={image.src}
          placeholderSrc={image.placeholderSrc}
          alt={image.alt}
          frameClassName="max-h-[82vh] border border-white/20 shadow-2xl"
          imageClassName="max-h-[82vh] max-w-full object-contain"
        />
      </div>
    </div>
  );
}

function DateBlock({ month, day, compact = false }) {
  const dayText = String(day);
  const daySize = dayText.length > 2 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl';
  const sizeClass = compact ? 'h-14 w-14 sm:h-16 sm:w-16' : 'h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]';

  return (
    <div className={`flex shrink-0 flex-col items-center justify-center border border-[#d5cfc0] bg-[#fffdf7] text-center ${sizeClass}`}>
      <span className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-[#a43b29]">
        {month}
      </span>
      <span className={`mt-1 font-serif leading-none text-[#1d2f24] ${daySize}`}>
        {dayText}
      </span>
    </div>
  );
}

function EventMeta({ event }) {
  return (
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#70806f]">
      {event.time && <span>{event.time}</span>}
      <span>{event.location}</span>
      <span>{event.year}</span>
    </div>
  );
}

function CategoryPill({ children }) {
  return (
    <span className="inline-flex w-fit border border-[#d8d0be] bg-[#f8f4ea] px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-[#8e3a2b]">
      {children}
    </span>
  );
}

function EventAdditionalDetails({ event }) {
  if (!event.additionalDescription && !event.freeStarts?.length) return null;

  return (
    <div className="mt-5 max-w-3xl border-l-2 border-[#8e3a2b] bg-[#f8f4ea] px-5 py-4 text-[#445044]">
      {event.additionalDescription && (
        <p className="text-sm leading-6 sm:text-base sm:leading-7">
          {event.additionalDescription}
        </p>
      )}
      {event.freeStarts?.length > 0 && (
        <div className={event.additionalDescription ? 'mt-4' : ''}>
          <h4 className="font-serif text-xl text-[#10351f]">Free Fall Garden Starts</h4>
          {event.freeStartsNote && (
            <p className="mt-1 text-sm leading-6">{event.freeStartsNote}</p>
          )}
          <ul className="mt-3 flex flex-wrap gap-2" aria-label="Available fall vegetable starts">
            {event.freeStarts.map((start) => (
              <li
                key={start}
                className="border border-[#d8d0be] bg-[#fffdf7] px-2.5 py-1 text-xs font-medium text-[#314935]"
              >
                {start}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FlyerButton({ event, onOpenImage, className = '' }) {
  if (!event.image) return null;

  return (
    <button
      type="button"
      onClick={() => onOpenImage({ src: event.image, alt: event.imageAlt || event.title })}
      className={`block overflow-hidden border border-[#d5cfc0] bg-[#f8f4ea] text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${className}`}
      aria-label={`Enlarge image for ${event.title}`}
    >
      <img
        src={event.image}
        alt={event.imageAlt || ''}
        className="h-auto w-full object-contain"
      />
    </button>
  );
}

function getEventShareUrl(eventId) {
  const eventPath = `/events#${encodeURIComponent(eventId)}`;

  if (typeof window === 'undefined') return eventPath;

  return new URL(eventPath, window.location.origin).href;
}

async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the older copy path below.
    }
  }

  if (typeof document === 'undefined') return false;

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';

  document.body.appendChild(textArea);
  textArea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function isLikelyMobileShareDevice() {
  if (typeof navigator === 'undefined') return false;

  if (navigator.userAgentData?.mobile) return true;

  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
}

function canUseNativeMobileShare(shareData) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (!isLikelyMobileShareDevice()) return false;

  if (typeof navigator.canShare === 'function') {
    return navigator.canShare(shareData);
  }

  return true;
}

function EventAnchorLink({ event, size = 'compact' }) {
  const [copyStatus, setCopyStatus] = useState('idle');
  const sizeClass =
    size === 'large'
      ? 'px-5 py-3 text-sm normal-case tracking-normal'
      : 'px-3 py-2 text-xs uppercase tracking-[0.18em]';
  const eventPath = `/events#${encodeURIComponent(event.id)}`;
  const label = copyStatus === 'copied' ? 'Copied URL' : copyStatus === 'error' ? 'Copy Failed' : 'Share';

  const handleClick = (clickEvent) => {
    if (clickEvent.metaKey || clickEvent.ctrlKey || clickEvent.shiftKey || clickEvent.altKey) return;

    clickEvent.preventDefault();

    const shareUrl = getEventShareUrl(event.id);
    const shareData = {
      title: event.title,
      text: event.title,
      url: shareUrl,
    };

    if (canUseNativeMobileShare(shareData)) {
      navigator.share(shareData).catch((error) => {
        if (error?.name !== 'AbortError') {
          copyTextToClipboard(shareUrl).then((copied) => {
            setCopyStatus(copied ? 'copied' : 'error');
          });
        }
      });
      return;
    }

    copyTextToClipboard(shareUrl).then((copied) => {
      setCopyStatus(copied ? 'copied' : 'error');
    });
  };

  useEffect(() => {
    if (copyStatus === 'idle') return undefined;

    const timeout = window.setTimeout(() => {
      setCopyStatus('idle');
    }, 2200);

    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  return (
    <Link
      to={eventPath}
      onClick={handleClick}
      className={`inline-flex w-fit items-center justify-center gap-2 border border-[#c9bfae] font-semibold text-[#5b695c] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] hover:text-[#10351f] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${sizeClass}`}
      aria-label={`Share link for ${event.title}`}
      title={getEventShareUrl(event.id)}
    >
      <FiShare2 className="h-4 w-4" aria-hidden="true" />
      <span aria-live="polite">{label}</span>
    </Link>
  );
}

function EventRegistrationLink({ event, size = 'compact' }) {
  if (!event.registrationUrl) return null;

  const sizeClass =
    size === 'large'
      ? 'px-5 py-3 text-sm'
      : 'px-3 py-2 text-xs uppercase tracking-[0.18em]';

  return (
    <a
      href={event.registrationUrl}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex w-fit items-center justify-center gap-2 border border-[#43513f] bg-[#10351f] font-semibold text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${sizeClass}`}
      aria-label={`Register for ${event.title}`}
    >
      <FiExternalLink className="h-4 w-4" aria-hidden="true" />
      <span>Register on Eventbrite</span>
    </a>
  );
}

function EventRegistrationCallout({ event, className = '' }) {
  if (!event.registrationUrl) return null;

  return (
    <div className={`border border-[#c9bfae] bg-[#f8f4ea] p-4 sm:flex sm:items-center sm:justify-between sm:gap-5 ${className}`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8e3a2b]">
          Registration required
        </p>
        <p className="mt-2 text-sm leading-6 text-[#445044]">
          {event.registrationNote || 'Registration is required for this event.'}
        </p>
      </div>
      <div className="mt-4 shrink-0 sm:mt-0">
        <EventRegistrationLink event={event} size="large" />
      </div>
    </div>
  );
}

function FeaturedEvent({ event, onOpenImage }) {
  if (!event) return null;

  return (
    <section id={event.id} className="scroll-mt-24 border-b border-[#d8d0be] bg-[#f8f4ea]">
      <div className="mx-auto max-w-[92rem] px-6 py-10 sm:px-10 md:py-14 lg:px-16">
        <div className="grid gap-8 border border-[#d5cfc0] bg-[#fffdf7] p-5 shadow-sm md:grid-cols-[minmax(0,1fr)_minmax(18rem,0.58fr)] md:p-8 lg:gap-12">
          <div className="min-w-0">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <CategoryPill>Next event</CategoryPill>
              {event.category && <CategoryPill>{event.category}</CategoryPill>}
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
              <DateBlock month={event.month} day={event.day} />
              <div className="min-w-0">
                <h2 className="font-serif text-3xl leading-tight text-[#10351f] sm:text-4xl lg:text-5xl">
                  {event.title}
                </h2>
                <EventMeta event={event} />
              </div>
            </div>

            {event.description && (
              <p className="mt-6 max-w-3xl text-base leading-7 text-[#445044] sm:text-lg sm:leading-8">
                {event.description}
              </p>
            )}

            <EventAdditionalDetails event={event} />

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <EventAnchorLink event={event} size="large" />
              {event.image && (
                <button
                  type="button"
                  onClick={() => onOpenImage({ src: event.image, alt: event.imageAlt || event.title })}
                  className="inline-flex items-center justify-center gap-2 border border-[#c9bfae] px-5 py-3 text-sm font-medium text-[#10351f] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
                >
                  <span>View Flyer</span>
                  <span aria-hidden="true">&rarr;</span>
                </button>
              )}
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-[#c9bfae] px-5 py-3 text-sm font-medium text-[#10351f] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
              >
                <span>Ask a Question</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>

          <FlyerButton event={event} onOpenImage={onOpenImage} className="md:self-start" />
          <EventRegistrationCallout event={event} className="md:col-span-2" />
        </div>
      </div>
    </section>
  );
}

function EventCard({ event, past = false, onOpenImage }) {
  return (
    <article
      id={event.id}
      className="group grid scroll-mt-24 gap-5 border border-[#d5cfc0] bg-[#fffdf7] p-5 transition-shadow hover:shadow-md sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-6 lg:grid-cols-[auto_minmax(0,1fr)_minmax(11rem,15rem)]"
    >
      <DateBlock month={event.month} day={event.day} compact />

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {event.category && <CategoryPill>{event.category}</CategoryPill>}
          {past && <CategoryPill>Past</CategoryPill>}
          <EventAnchorLink event={event} />
        </div>
        <div className="mt-3 flex items-start justify-between gap-3">
          <h3 className="font-serif text-2xl leading-tight text-[#18271e] sm:text-3xl">
            {event.title}
          </h3>
          {!past && (
            <span
              className="mt-1 text-xl leading-none text-[#a43b29] transition-transform group-hover:translate-x-1"
              aria-hidden="true"
            >
              &rarr;
            </span>
          )}
        </div>

        <EventMeta event={event} />

        {event.description && (
          <p className="mt-4 max-w-3xl text-base leading-7 text-[#445044]">
            {event.description}
          </p>
        )}

        <EventAdditionalDetails event={event} />

        {event.tags?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {event.tags.map((tag) => (
              <span
                key={tag}
                className="border border-[#e1dacd] bg-[#fbf8ef] px-2.5 py-1 text-xs text-[#5b695c]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <FlyerButton event={event} onOpenImage={onOpenImage} className="sm:col-start-2 lg:col-start-auto lg:row-span-2 lg:self-start" />
      {!past && (
        <EventRegistrationCallout event={event} className="sm:col-span-2 lg:col-span-3" />
      )}
    </article>
  );
}

function BotanicalAccent() {
  return (
    <div className="pointer-events-none hidden opacity-50 md:block" aria-hidden="true">
      <svg viewBox="0 0 180 132" className="h-32 w-44 text-[#314935]">
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2">
          <path d="M28 116 C34 82 36 52 29 22" />
          <path d="M31 77 C17 67 12 55 15 40 C31 44 39 54 35 70" />
          <path d="M34 56 C47 47 53 35 51 18 C36 22 27 36 31 51" />
          <path d="M74 116 C76 80 80 50 96 18" />
          <path d="M79 76 C64 66 60 52 66 39 C80 45 86 57 82 71" />
          <path d="M91 44 C108 43 120 34 125 18 C108 16 96 24 91 39" />
          <path d="M128 116 C126 88 132 58 154 26" />
          <path d="M132 80 C148 78 162 68 168 53 C151 50 139 61 134 74" />
          <path d="M144 48 C132 38 129 26 134 12 C147 17 154 29 151 43" />
          <circle cx="26" cy="20" r="7" />
          <circle cx="96" cy="17" r="8" />
          <circle cx="155" cy="24" r="7" />
        </g>
      </svg>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-[#cfc7b6] px-8 py-14 text-center">
      <p className="font-serif text-2xl text-[#8e9e8e]">Nothing here yet</p>
      <p className="mt-2 text-sm text-[#70806f]">{label}</p>
    </div>
  );
}

function eventSearchText(event) {
  return [
    event.title,
    event.category,
    event.details,
    event.location,
    event.time,
    event.year,
    event.description,
    event.additionalDescription,
    event.freeStartsNote,
    ...(event.freeStarts || []),
    event.registrationNote,
    ...(event.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function groupEventsByMonth(events) {
  return events.reduce((groups, event) => {
    const key = `${event.month} ${event.year}`;
    const existing = groups.find((group) => group.label === key);
    if (existing) {
      existing.events.push(event);
    } else {
      groups.push({ label: key, events: [event] });
    }
    return groups;
  }, []);
}

function countForView(viewId) {
  if (viewId === 'upcoming') return upcomingEvents.length;
  if (viewId === 'past') return pastEvents.length;
  return upcomingEvents.length + pastEvents.length;
}

function EventsPage() {
  const location = useLocation();
  const hashEventId = decodeURIComponent(location.hash.replace(/^#/, ''));
  const [lightboxImage, setLightboxImage] = useState(null);
  const [viewFilter, setViewFilter] = useState(DEFAULT_VIEW_FILTER);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const allEvents = useMemo(() => [...upcomingEvents, ...pastEvents], []);
  const featuredEvent = upcomingEvents[0];
  const categoryOptions = useMemo(() => {
    const categories = allEvents.map((event) => event.category).filter(Boolean);
    return ['All', ...Array.from(new Set(categories))];
  }, [allEvents]);

  const visibleEvents = useMemo(() => {
    const sourceEvents =
      viewFilter === 'upcoming'
        ? upcomingEvents
        : viewFilter === 'past'
          ? pastEvents
          : allEvents;
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return sourceEvents.filter((event) => {
      const matchesCategory = categoryFilter === 'All' || event.category === categoryFilter;
      const matchesSearch = !normalizedQuery || eventSearchText(event).includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [allEvents, categoryFilter, searchQuery, viewFilter]);

  const isDefaultUpcomingView =
    viewFilter === 'upcoming' && categoryFilter === 'All' && searchQuery.trim() === '';
  const listedEvents = isDefaultUpcomingView ? upcomingEvents.slice(1) : visibleEvents;
  const groupedEvents = useMemo(() => groupEventsByMonth(listedEvents), [listedEvents]);
  const resultCount = visibleEvents.length;

  const clearFilters = () => {
    setViewFilter(DEFAULT_VIEW_FILTER);
    setCategoryFilter('All');
    setSearchQuery('');
  };

  useEffect(() => {
    if (!hashEventId) return;

    setViewFilter(DEFAULT_VIEW_FILTER);
    setCategoryFilter('All');
    setSearchQuery('');
  }, [hashEventId]);

  useEffect(() => {
    if (!hashEventId) return;

    window.requestAnimationFrame(() => {
      document.getElementById(hashEventId)?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }, [hashEventId, viewFilter]);

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">
      <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />

      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-12 sm:px-10 md:py-16 lg:px-16">
          <div className="flex items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl md:text-7xl">
                Events
              </h1>
              <div className="mt-5 h-px w-24 bg-[#304536]" />
              <p className="font-handwritten mt-5 max-w-xl text-xl leading-snug text-[#243c2c] sm:text-2xl">
                Garden events, volunteer days, harvests, and community gatherings.
              </p>
            </div>
            <BotanicalAccent />
          </div>
        </div>
      </section>

      {isDefaultUpcomingView && (
        <FeaturedEvent event={featuredEvent} onOpenImage={setLightboxImage} />
      )}

      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto max-w-[92rem] px-6 py-12 sm:px-10 md:py-16 lg:px-16">
          <div className="mb-8 border-b border-[#cfc7b6] pb-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                  Event directory
                </p>
                <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
                  Browse Events
                </h2>
              </div>
              <p className="max-w-xl text-sm leading-6 text-[#5b695c]">
                Search by event name, location, program, or tag. Filter by event type as this list grows.
              </p>
            </div>
          </div>

          <div className="mb-8 border border-[#d5cfc0] bg-[#fffdf7] p-4 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.24em] text-[#70806f]">
                  Search events
                </span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by title, location, program, or tag"
                  className="mt-2 w-full border border-[#cfc7b6] bg-[#fbf8ef] px-4 py-3 text-base text-[#18271e] outline-none transition focus:border-[#8e3a2b] focus:ring-2 focus:ring-[#8e3a2b]/25"
                />
              </label>

              <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[23rem]">
                {VIEW_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setViewFilter(filter.id)}
                    className={`border px-4 py-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${
                      viewFilter === filter.id
                        ? 'border-[#10351f] bg-[#10351f] text-[#fbf8ef]'
                        : 'border-[#cfc7b6] text-[#10351f] hover:border-[#8e3a2b] hover:bg-[#f4efe3]'
                    }`}
                  >
                    {filter.label} <span className="text-xs opacity-75">({countForView(filter.id)})</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {categoryOptions.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setCategoryFilter(category)}
                  className={`border px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-colors focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${
                    categoryFilter === category
                      ? 'border-[#8e3a2b] bg-[#8e3a2b] text-white'
                      : 'border-[#d8d0be] text-[#5b695c] hover:border-[#8e3a2b] hover:text-[#10351f]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="mt-5 flex flex-col gap-3 border-t border-[#e1dacd] pt-4 text-sm text-[#5b695c] sm:flex-row sm:items-center sm:justify-between">
              <p>
                Showing <span className="font-semibold text-[#10351f]">{resultCount}</span>{' '}
                {resultCount === 1 ? 'event' : 'events'}
              </p>
              {(viewFilter !== DEFAULT_VIEW_FILTER || categoryFilter !== 'All' || searchQuery.trim()) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="w-fit border border-[#cfc7b6] px-3 py-2 text-sm font-medium text-[#10351f] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div>
            <h3 className="mb-5 font-serif text-3xl text-[#10351f]">
              {isDefaultUpcomingView ? 'More Upcoming Events' : 'Event Results'}
            </h3>

            {groupedEvents.length > 0 ? (
              <div className="grid gap-9">
                {groupedEvents.map((group) => (
                  <section key={group.label}>
                    <div className="mb-4 flex items-center gap-4">
                      <h4 className="shrink-0 text-xs font-semibold uppercase tracking-[0.28em] text-[#8e3a2b]">
                        {group.label}
                      </h4>
                      <div className="h-px flex-1 bg-[#cfc7b6]" />
                    </div>
                    <div className="grid gap-5">
                      {group.events.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          past={pastEvents.some((pastEvent) => pastEvent.id === event.id)}
                          onOpenImage={setLightboxImage}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <EmptyState label="No events match those filters." />
            )}
          </div>
        </div>
      </section>

      <section className="border-t border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-16 lg:px-16">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-serif text-3xl text-[#10351f] sm:text-4xl">
                Have an event question?
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#5b695c]">
                Reach out about volunteering, event details, or getting an event listed.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-sm font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:justify-center sm:gap-3"
            >
              <span>Get in Touch</span>
              <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EventsPage;
